import { CheckCircle, XCircle, Loader2, AlertCircle, Pause, Play, Trash2, RotateCcw, Wrench, Globe, Archive, Search, Activity, Clock, AlertTriangle, FileWarning } from 'lucide-react';
import Sidebar from '../components/ui/Sidebar';
import { getCamundaService } from '../../infrastructure/camunda';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface ProcessInstance {
  id: string;
  [key: string]: any;
}

interface DeleteResult {
  id: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
}

const ToolsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active tab from URL path
  const activeTab = useMemo(() => {
    const path = location.pathname;
    if (path.includes('/tools/deployments')) return 'deployments';
    if (path.includes('/tools/system-tools')) return 'systemTools';
    return 'processCleanup';
  }, [location.pathname]);

  const tabs = [
    { key: 'processCleanup', label: 'Process Cleanup', icon: <Wrench size={18} />, path: '/tools' },
    { key: 'deployments', label: 'Deployments', icon: <Archive size={18} />, path: '/tools/deployments' },
    { key: 'systemTools', label: 'System Tools', icon: <Globe size={18} />, path: '/tools/system-tools' },
  ];
  // State management with proper typing
  const [deleteResults, setDeleteResults] = useState<DeleteResult[]>([]);
  const [currentStep, setCurrentStep] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [processDefinitionId, setProcessDefinitionId] = useState('MAX:1:9be5cd56-a421-11f0-b5f9-9e75511e0b7e');
  const [instanceType, setInstanceType] = useState<'running' | 'history'>('running');
  const batchSize = 50; // Fixed batch size
  const [concurrency, setConcurrency] = useState<number>(10);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [endTime, setEndTime] = useState<number | null>(null);
  const [operationsPerSecond, setOperationsPerSecond] = useState<number>(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<string>('Calculating...');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isRefreshingCount, setIsRefreshingCount] = useState<boolean>(false);
  const [processDefinitions, setProcessDefinitions] = useState<any[]>([]);
  const [isLoadingDefinitions, setIsLoadingDefinitions] = useState<boolean>(false);

  // Refs
  const pauseRef = useRef(false);
  const stopRef = useRef(false);
  const BASE_URL = 'https://portal-eroyal.pmdgtech.com/engine/engine-rest';

  const fetchCount = async (): Promise<number> => {
    setCurrentStep('Fetching process instance count...');
    const endpoint = instanceType === 'running'
      ? `${BASE_URL}/process-instance/count`
      : `${BASE_URL}/history/process-instance/count`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        processDefinitionId: processDefinitionId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch count: ${response.statusText}`);
    }

    const data = await response.json();
    setTotalCount(data.count);
    return data.count;
  };

  const handleRefreshCount = async () => {
    setIsRefreshingCount(true);
    try {
      await fetchCount();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
    } finally {
      setIsRefreshingCount(false);
    }
  };

  const getDefinitionId = (d: any): string | undefined => {
    return d?.processDefinitionId ?? d?.definition?.id ?? d?.id;
  };

  const getDefinitionName = (d: any): string => {
    return (
      d?.definition?.name ?? d?.name ?? d?.definition?.key ?? d?.key ?? getDefinitionId(d) ?? 'Unknown'
    );
  };

  const fetchProcessDefinitions = async (): Promise<void> => {
    setIsLoadingDefinitions(true);
    setCurrentStep('Fetching process definitions...');
    try {
      const url = `${BASE_URL}/process-definition/statistics?firstResult=0&maxResults=50&nameLike=%25afc%25&sortBy=name&sortOrder=asc`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch process definitions: ${response.statusText}`);
      }
      const data = await response.json();
      setProcessDefinitions(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.length > 0) {
        const firstId = getDefinitionId(data[0]);
        if (firstId) {
          setProcessDefinitionId(firstId);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
    } finally {
      setIsLoadingDefinitions(false);
    }
  };

  useEffect(() => {
    fetchProcessDefinitions();
  }, []);

  const fetchInstances = async (maxResults: number, firstResult: number = 0): Promise<ProcessInstance[]> => {
    setCurrentStep('Fetching process instances...');
    const endpoint = instanceType === 'running'
      ? `${BASE_URL}/process-instance`
      : `${BASE_URL}/history/process-instance`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        maxResults,
        firstResult,
        processDefinitionId: processDefinitionId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch instances: ${response.statusText}`);
    }

    return await response.json();
  };

  const deleteInstance = async (id: string): Promise<void> => {
    while (pauseRef.current) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    setCurrentStep('Deleting process instance...');
    const endpoint = instanceType === 'running'
      ? `${BASE_URL}/process-instance/${id}?skipCustomListeners=true&skipIoMappings=true`
      : `${BASE_URL}/history/process-instance/${id}?cascade=true&skipCustomListeners=true&skipIoMappings=true`;

    const response = await fetch(endpoint, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete instance: ${response.statusText}`);
    }
  };

  // State for tracking pause status
  const [isPaused, setIsPaused] = useState<boolean>(false);

  // Pause functionality
  const togglePause = () => {
    if (stopRef.current) return;

    const newPausedState = !pauseRef.current;
    pauseRef.current = newPausedState;
    setIsPaused(newPausedState);

    if (newPausedState) {
      setCurrentStep('Process paused. Click Resume to continue...');
    } else {
      setCurrentStep('Resuming process...');
    }
  };

  const processBatch = async (batch: ProcessInstance[]): Promise<number> => {
    const batchStartTime = Date.now();
    let completed = 0;

    for (let i = 0; i < batch.length; i += concurrency) {
      if (stopRef.current) {
        return completed;
      }

      while (pauseRef.current && !stopRef.current) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      if (stopRef.current) {
        return completed;
      }

      const chunk = batch.slice(i, i + concurrency);
      const updateMetrics = () => {
        const elapsed = (Date.now() - batchStartTime) / 1000;
        const currentOpsPerSecond = completed / Math.max(1, elapsed);
        setOperationsPerSecond(currentOpsPerSecond);

        const remainingInstances = batch.length - completed;
        if (currentOpsPerSecond > 0) {
          const remainingTime = remainingInstances / currentOpsPerSecond;
          setEstimatedTimeRemaining(formatDuration(remainingTime));
        }

        return currentOpsPerSecond;
      };

      const chunkPromises = chunk.map(async (instance) => {
        try {
          await processInstance(instance);
          return true;
        } catch (error) {
          console.error(`Error processing instance ${instance.id}:`, error);
          return false;
        } finally {
          completed++;
          updateMetrics();
        }
      });

      await Promise.all(chunkPromises);
      updateMetrics();

      if (pauseRef.current) {
        await new Promise<void>((resolve) => {
          const checkPause = () => {
            if (!pauseRef.current) resolve();
            else setTimeout(checkPause, 100);
          };
          checkPause();
        });
      }
    }

    return completed;
  };

  const processInstance = async (instance: ProcessInstance): Promise<void> => {
    if (pauseRef.current) {
      await new Promise<void>((resolve) => {
        const checkPause = () => {
          if (!pauseRef.current) resolve();
          else setTimeout(checkPause, 100);
        };
        checkPause();
      });
    }
    try {
      updateResult(instance.id, { status: 'pending' });
      await deleteInstance(instance.id);
      updateResult(instance.id, { status: 'success' });
    } catch (err) {
      updateResult(instance.id, {
        status: 'error',
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      throw err;
    }
  };

  const updateResult = (id: string, update: Partial<DeleteResult>) => {
    setDeleteResults(prev => {
      const newResults = [...prev];
      const existingIndex = newResults.findIndex(r => r.id === id);

      if (existingIndex !== -1) {
        newResults[existingIndex] = { ...newResults[existingIndex], ...update };
      } else {
        newResults.push({ id, status: 'pending', ...update });
      }

      return newResults;
    });
  };

  const deleteAllInstances = async (instancesToDelete: ProcessInstance[]): Promise<number> => {
    const batchStartTime = Date.now();

    const initialResults: DeleteResult[] = instancesToDelete.map(instance => ({
      id: instance.id,
      status: 'pending' as const,
    }));

    setDeleteResults(prev => [...prev, ...initialResults]);

    const processedCount = await processBatch(instancesToDelete);

    const batchEndTime = Date.now();
    const batchDuration = (batchEndTime - batchStartTime) / 1000;
    const currentResults = deleteResults;
    const successCount = currentResults.filter(r => r.status === 'success').length;
    const errorCount = currentResults.filter(r => r.status === 'error').length;

    setCurrentStep(`Batch completed in ${formatDuration(batchDuration)}: ${successCount} succeeded, ${errorCount} failed`);
    setOperationsPerSecond(instancesToDelete.length / Math.max(1, batchDuration));

    return processedCount;
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const remainingMinutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${remainingMinutes}m`;
  };

  const stopProcess = () => {
    stopRef.current = true;
    pauseRef.current = false;
    setIsPaused(false);
    setIsLoading(false);
    setCurrentStep('Process stopped by user');
  };

  const resetProcess = () => {
    stopRef.current = false;
    pauseRef.current = false;
    setIsPaused(false);
    setDeleteResults([]);
    setStartTime(null);
    setEndTime(null);
    setTotalCount(null);
    setOperationsPerSecond(0);
    setEstimatedTimeRemaining('Calculating...');
    setCurrentStep('');
    setError('');
  };

  const handleExecute = async () => {
    if (!processDefinitionId) {
      setError('Please enter a process definition ID');
      return;
    }

    if (concurrency < 1 || concurrency > 50) {
      setError('Concurrency must be between 1 and 50');
      return;
    }

    resetProcess();
    setIsLoading(true);
    stopRef.current = false;
    const processStartTime = Date.now();
    setStartTime(processStartTime);
    setCurrentStep(`Process started at ${new Date(processStartTime).toLocaleTimeString()}`);

    try {
      const totalCount = await fetchCount();

      if (totalCount === 0) {
        setCurrentStep('No instances to delete');
        return;
      }

      const calculatedTotalBatches = Math.ceil(totalCount / batchSize);

      setStartTime(processStartTime);

      for (let batch = 0; batch < calculatedTotalBatches; batch++) {
        if (stopRef.current) {
          const endTime = Date.now();
          setCurrentStep(`Process stopped.`);
          setEndTime(endTime);
          setIsLoading(false);
          return;
        }

        while (pauseRef.current && !stopRef.current) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        const firstResult = batch * batchSize;
        const currentBatchSize = Math.min(batchSize, totalCount - firstResult);

        const fetchedInstances = await fetchInstances(currentBatchSize, firstResult);

        await deleteAllInstances(fetchedInstances);

        setCurrentStep(`Processed batch ${batch + 1} of ${calculatedTotalBatches}`);

        if (batch < calculatedTotalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      const endTime = Date.now();
      setCurrentStep(`All operations completed in ${formatDuration((endTime - (startTime || 0)) / 1000)}. Successfully processed ${deleteResults.filter(r => r.status === 'success').length} instances.`);
      setEndTime(endTime);
      setIsLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      setCurrentStep(`Error: ${errorMessage}`);
    }
  };

  const getStatusIcon = (status: DeleteResult['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'pending':
        return <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />;
    }
  };

  const { successCount, errorCount } = useMemo(() => {
    return deleteResults.reduce(
      (acc, result) => {
        if (result.status === 'success') acc.successCount++;
        else if (result.status === 'error') acc.errorCount++;
        return acc;
      },
      { successCount: 0, errorCount: 0 }
    );
  }, [deleteResults]);

  // Deployment State
  const [deployments, setDeployments] = useState<any[]>([]);
  const [filteredDeployments, setFilteredDeployments] = useState<any[]>([]);
  const [isLoadingDeployments, setIsLoadingDeployments] = useState<boolean>(false);
  const [deploymentsError, setDeploymentsError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [systemStats, setSystemStats] = useState<{ running: number; incidents: number; history: number } | null>(null);

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    deploymentId: string | null;
    runningCount: number;
    historyCount: number;
    isChecking: boolean;
  }>({
    isOpen: false,
    deploymentId: null,
    runningCount: 0,
    historyCount: 0,
    isChecking: false,
  });

  const camunda = getCamundaService();

  const fetchSystemStats = async () => {
    try {
      const [stats, historyPayload] = await Promise.all([
        camunda.processDefinition.getStatistics({ incidents: true }),
        camunda.history.getProcessInstancesCount({})
      ]);

      const running = stats.reduce((sum, def) => sum + def.instances, 0);
      const incidents = stats.reduce((sum, def) => sum + (def.incidents?.reduce((iSum, inc) => iSum + inc.incidentCount, 0) || 0), 0);

      setSystemStats({
        running,
        incidents,
        history: historyPayload.count
      });
    } catch (err) {
      console.error('Failed to fetch system stats', err);
    }
  };

  const fetchDeployments = async () => {
    setIsLoadingDeployments(true);
    setDeploymentsError('');
    try {
      const data = await camunda.deployment.getList({
        sortBy: 'deploymentTime',
        sortOrder: 'desc'
      });
      setDeployments(data);
      setFilteredDeployments(data);
      // Refresh stats when deployments are fetched
      fetchSystemStats();
    } catch (err) {
      setDeploymentsError(err instanceof Error ? err.message : 'Failed to fetch deployments');
    } finally {
      setIsLoadingDeployments(false);
    }
  };

  useEffect(() => {
    if (deployments.length > 0) {
      const lowerQuery = searchQuery.toLowerCase();
      const filtered = deployments.filter(d =>
        d.id.toLowerCase().includes(lowerQuery) ||
        (d.name && d.name.toLowerCase().includes(lowerQuery)) ||
        (d.source && d.source.toLowerCase().includes(lowerQuery))
      );
      setFilteredDeployments(filtered);
    }
  }, [searchQuery, deployments]);

  const handleVerifyDelete = async (id: string) => {
    setDeleteModal({
      isOpen: true,
      deploymentId: id,
      runningCount: 0,
      historyCount: 0,
      isChecking: true,
    });

    try {
      // Fetch definitions first to get accurate counts per definition
      const definitions = await camunda.processDefinition.getList({ deploymentId: id });

      let totalRunning = 0;
      let totalHistory = 0;

      if (definitions.length > 0) {
        const counts = await Promise.all(
          definitions.map(async (def) => {
            const [run, hist] = await Promise.all([
              camunda.processInstance.getCount({ processDefinitionId: def.id }),
              camunda.history.getProcessInstancesCount({ processDefinitionId: def.id })
            ]);
            return { run: run.count, hist: hist.count };
          })
        );

        totalRunning = counts.reduce((acc, curr) => acc + curr.run, 0);
        totalHistory = counts.reduce((acc, curr) => acc + curr.hist, 0);
      } else {
        // Fallback or DMN/CMMN cases might need handling, but for now try deploymentId as backup
        // if no process definitions found (though unlikely for process apps)
        const [runningRes, historyRes] = await Promise.all([
          camunda.processInstance.getCount({ deploymentId: id }),
          camunda.history.getProcessInstancesCount({ deploymentId: id })
        ]);
        totalRunning = runningRes.count;
        totalHistory = historyRes.count;
      }

      setDeleteModal(prev => ({
        ...prev,
        runningCount: totalRunning,
        historyCount: totalHistory,
        isChecking: false,
      }));
    } catch (err) {
      console.error('Failed to check checks', err);
      // In case of error, stop checking but keep modal open
      setDeleteModal(prev => ({ ...prev, isChecking: false }));
    }
  };

  const confirmDeleteDeployment = async () => {
    if (!deleteModal.deploymentId) return;

    const id = deleteModal.deploymentId;
    // Optimistic UI updates not suitable here due to complex backend logic potentially failing
    try {
      await camunda.deployment.delete(id, { cascade: true });
      setDeployments(prev => prev.filter(d => d.id !== id));
      setDeleteModal({ isOpen: false, deploymentId: null, runningCount: 0, historyCount: 0, isChecking: false });
      // Refresh stats
      fetchSystemStats();
    } catch (err) {
      alert('Failed to delete deployment: ' + (err instanceof Error ? err.message : 'Unknown error'));
      setDeleteModal({ isOpen: false, deploymentId: null, runningCount: 0, historyCount: 0, isChecking: false });
    }
  };

  useEffect(() => {
    if (activeTab === 'deployments') {
      fetchDeployments();
    }
  }, [activeTab]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'processCleanup':
        return (
          <div className="space-y-6">
            {/* Configuration Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Wrench size={20} className="text-blue-600" />
                  Cleanup Configuration
                </h2>
                <p className="text-sm text-gray-500 mt-1">Select process definitions and configure deletion parameters.</p>
              </div>

              <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column: Selection */}
                <div className="space-y-6">
                  <div>
                    <label htmlFor="processDefinitionSelect" className="block text-sm font-medium text-gray-700 mb-2">
                      Process Definition
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <select
                          id="processDefinitionSelect"
                          value={processDefinitionId}
                          onChange={(e) => setProcessDefinitionId(e.target.value)}
                          className="w-full pl-3 pr-10 py-2.5 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-all appearance-none"
                          disabled={isLoadingDefinitions || isLoading}
                        >
                          {processDefinitions.length === 0 ? (
                            <option value="" disabled>
                              {isLoadingDefinitions ? 'Loading definitions...' : 'No definitions found'}
                            </option>
                          ) : (
                            processDefinitions.map((d, idx) => {
                              const id = getDefinitionId(d);
                              const label = getDefinitionName(d);
                              return (
                                <option key={id ?? idx} value={id ?? ''}>
                                  {label} ({d.key})
                                </option>
                              );
                            })
                          )}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-gray-500">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={fetchProcessDefinitions}
                        className="inline-flex items-center justify-center p-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors disabled:opacity-50"
                        disabled={isLoadingDefinitions || isLoading}
                        title="Refresh process definitions"
                      >
                        {isLoadingDefinitions ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <RotateCcw className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    <p className="mt-1.5 text-xs font-mono text-gray-500 break-all bg-gray-50 p-1 rounded border border-gray-100">{processDefinitionId}</p>
                  </div>

                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-3">Instance Type to Delete</span>
                    <div className="flex gap-4">
                      <label className={`flex-1 relative flex items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all ${instanceType === 'running' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}>
                        <input
                          type="radio"
                          className="sr-only"
                          checked={instanceType === 'running'}
                          onChange={() => setInstanceType('running')}
                          disabled={isLoading}
                        />
                        <Activity size={18} className="mr-2" />
                        <span className="font-medium">Running Instances</span>
                      </label>
                      <label className={`flex-1 relative flex items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all ${instanceType === 'history' ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-gray-300'}`}>
                        <input
                          type="radio"
                          className="sr-only"
                          checked={instanceType === 'history'}
                          onChange={() => setInstanceType('history')}
                          disabled={isLoading}
                        />
                        <Clock size={18} className="mr-2" />
                        <span className="font-medium">Historical Instances</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Right Column: Settings & Actions */}
                <div className="flex flex-col justify-between space-y-6">
                  <div>
                    <label htmlFor="concurrency" className="block text-sm font-medium text-gray-700 mb-2">
                      Concurrency Limit (1-50)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        id="concurrency"
                        min="1"
                        max="50"
                        value={concurrency}
                        onChange={(e) => setConcurrency(parseInt(e.target.value) || 10)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 transition-all font-mono"
                        disabled={isLoading}
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400 text-xs">
                        THREADS
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">Higher concurrency increases speed but puts more load on the engine.</p>
                  </div>

                  <div className="pt-4 border-t border-gray-100">
                    {!isLoading ? (
                      <button
                        onClick={handleExecute}
                        className="w-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white font-semibold py-3.5 px-6 rounded-xl shadow-md transition-all transform hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2.5"
                      >
                        <Trash2 className="w-5 h-5" />
                        Start Cleanup Process
                      </button>
                    ) : (
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={stopProcess}
                          className="flex-1 bg-white border-2 border-red-500 text-red-600 hover:bg-red-50 font-semibold py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                          disabled={stopRef.current}
                        >
                          <XCircle className="w-5 h-5" />
                          Stop
                        </button>

                        <button
                          type="button"
                          onClick={togglePause}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl shadow-md transition-colors flex items-center justify-center gap-2"
                          disabled={stopRef.current}
                          title={isPaused ? 'Resume' : 'Pause'}
                        >
                          {isPaused ? (
                            <> <Play className="w-5 h-5" /> Resume </>
                          ) : (
                            <> <Pause className="w-5 h-5" /> Pause </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-900 text-sm">Operation Error</h4>
                  <p className="text-red-700 text-sm mt-0.5">{error}</p>
                </div>
              </div>
            )}

            {/* Monitoring Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Card */}
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm font-medium text-gray-500">Total Found</span>
                  <button
                    type="button"
                    onClick={handleRefreshCount}
                    className="text-gray-400 hover:text-blue-600 transition-colors"
                    disabled={isRefreshingCount || isLoading}
                  >
                    <RotateCcw className={`w-4 h-4 ${isRefreshingCount ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                <div className="text-2xl font-bold text-gray-900">{totalCount !== null ? totalCount.toLocaleString() : '-'}</div>
              </div>

              {/* Ops/Sec */}
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm font-medium text-gray-500">Speed (Ops/Sec)</span>
                  <Activity className="w-4 h-4 text-green-500" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{operationsPerSecond.toFixed(1)}</div>
                {/* Tiny visual chart or progress bar could go here */}
                <div className="absolute bottom-0 left-0 h-1 bg-green-500 transition-all duration-500" style={{ width: `${Math.min(operationsPerSecond * 2, 100)}%` }}></div>
              </div>

              {/* ETA */}
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm font-medium text-gray-500">Est. Time Remaining</span>
                  <Clock className="w-4 h-4 text-blue-500" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{estimatedTimeRemaining}</div>
              </div>

              {/* Success/Error */}
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm font-medium text-gray-500">Success / Failed</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-green-600">{successCount.toLocaleString()}</span>
                  <span className="text-sm text-gray-400">/</span>
                  <span className="text-2xl font-bold text-red-600">{errorCount.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Status Bar */}
            {currentStep && (
              <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isLoading && <Loader2 className="w-4 h-4 text-indigo-600 animate-spin" />}
                  <p className="text-indigo-900 font-medium text-sm">{currentStep}</p>
                </div>
                {endTime && (
                  <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-md font-medium">Completed</span>
                )}
              </div>
            )}

            {/* Detailed Progress Table */}
            {deleteResults.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                  <h3 className="font-semibold text-gray-900">Operation Details</h3>
                  <div className="text-xs text-gray-500 font-mono">Showing last batch details</div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {/* We can limit this list or virtualize it if it gets too long, showing strict subset for now */}
                  <div className="divide-y divide-gray-100">
                    {deleteResults.slice(-50).reverse().map((result) => (
                      <div key={result.id} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(result.status)}
                          <span className="text-sm font-mono text-gray-600">{result.id}</span>
                        </div>
                        {result.error && (
                          <span className="text-xs text-red-500 max-w-xs truncate" title={result.error}>
                            {result.error}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-3 bg-gray-50 text-center text-xs text-gray-400 border-t border-gray-100">
                  Showing latest 50 operations
                </div>
              </div>
            )}
          </div>
        );
      case 'deployments':
        const isHighRisk = (deleteModal.runningCount + deleteModal.historyCount) > 100;

        return (
          <div className="space-y-6 relative">
            {/* Stats Dashboard */}
            {systemStats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in-up">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg transform transition-transform hover:scale-[1.02]">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-blue-100 text-sm font-medium mb-1">Running Instances</p>
                      <h3 className="text-3xl font-bold">{systemStats.running.toLocaleString()}</h3>
                    </div>
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Activity className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-5 text-white shadow-lg transform transition-transform hover:scale-[1.02]">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-red-100 text-sm font-medium mb-1">Total Incidents</p>
                      <h3 className="text-3xl font-bold">{systemStats.incidents.toLocaleString()}</h3>
                    </div>
                    <div className="p-2 bg-white/20 rounded-lg">
                      <AlertTriangle className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-5 text-white shadow-lg transform transition-transform hover:scale-[1.02]">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-indigo-100 text-sm font-medium mb-1">Historical Instances</p>
                      <h3 className="text-3xl font-bold">{systemStats.history.toLocaleString()}</h3>
                    </div>
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Active Deployments</h3>
                  <p className="text-sm text-gray-500">Manage and monitor system deployments</p>
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto">
                  <div className="relative flex-1 md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search deployments..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <button
                    onClick={() => { fetchDeployments(); fetchSystemStats(); }}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-gray-200"
                    title="Refresh data"
                  >
                    <RotateCcw size={18} className={isLoadingDeployments ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>

              {deploymentsError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{deploymentsError}</p>
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoadingDeployments ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="h-5 w-20 bg-gray-200 rounded"></div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="h-5 w-32 bg-gray-200 rounded"></div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="h-5 w-36 bg-gray-200 rounded"></div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="h-5 w-16 bg-gray-200 rounded"></div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="h-8 w-8 bg-gray-200 rounded-lg ml-auto"></div>
                          </td>
                        </tr>
                      ))
                    ) : filteredDeployments.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 py-12">
                          <div className="flex flex-col items-center justify-center gap-2">
                            <Archive className="w-8 h-8 text-gray-300" />
                            <p>No deployments found matching your search</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredDeployments.map((deployment) => (
                        <tr key={deployment.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <span title={deployment.id} className="font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded text-xs">
                              {deployment.id.substring(0, 8)}...
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                            {deployment.name || <span className="text-gray-400 italic">Unnamed</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {deployment.deploymentTime ? new Date(deployment.deploymentTime).toLocaleString() : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {deployment.source ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                {deployment.source}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleVerifyDelete(deployment.id)}
                              className="text-gray-400 hover:text-red-600 p-2 hover:bg-red-50 rounded-lg transition-all"
                              title="Delete Deployment"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Delete Modal */}
            {deleteModal.isOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
                <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
                  <div className={`p-6 ${isHighRisk ? 'bg-red-50' : 'bg-gray-50'} border-b ${isHighRisk ? 'border-red-100' : 'border-gray-100'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-full ${isHighRisk ? 'bg-red-100 text-red-600' : 'bg-gray-200 text-gray-600'}`}>
                        {isHighRisk ? <FileWarning className="w-6 h-6" /> : <Trash2 className="w-6 h-6" />}
                      </div>
                      <div>
                        <h3 className={`text-lg font-bold ${isHighRisk ? 'text-red-900' : 'text-gray-900'}`}>Delete Deployment?</h3>
                        <p className={`text-sm ${isHighRisk ? 'text-red-600' : 'text-gray-500'}`}>
                          {isHighRisk ? 'High Risk Action Detected' : 'This action cannot be undone'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    {deleteModal.isChecking ? (
                      <div className="flex flex-col items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-2" />
                        <p className="text-gray-500 text-sm">Analyzing impact...</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <p className="text-gray-600">
                          You are about to delete deployment <span className="font-mono font-medium text-gray-900 bg-gray-100 px-1 rounded">{deleteModal.deploymentId}</span>.
                        </p>

                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Impact Analysis</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-2xl font-bold text-gray-900">{deleteModal.runningCount}</div>
                              <div className="text-xs text-gray-500">Running Instances</div>
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-gray-900">{deleteModal.historyCount}</div>
                              <div className="text-xs text-gray-500">Historical Instances</div>
                            </div>
                          </div>
                        </div>

                        {isHighRisk && (
                          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3 text-red-700 text-sm">
                            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                            <p>
                              <strong>Caution:</strong> This deployment has over 100 associated instances.
                              Deleting it may degrade system performance while the cascade deletion is processing.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                    <button
                      onClick={() => setDeleteModal({ isOpen: false, deploymentId: null, runningCount: 0, historyCount: 0, isChecking: false })}
                      className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDeleteDeployment}
                      disabled={deleteModal.isChecking}
                      className={`px-4 py-2 text-white font-medium rounded-lg shadow-sm transition-colors flex items-center gap-2 ${isHighRisk
                        ? 'bg-red-600 hover:bg-red-700 focus:ring-4 focus:ring-red-200'
                        : 'bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-200'
                        }`}
                    >
                      {deleteModal.isChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {isHighRisk ? 'I Understand, Delete Everything' : 'Confirm Delete'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'systemTools':
        return (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">System Tools</h3>
            <div className="space-y-4">
              <div className="p-4 border border-gray-200 rounded-lg">
                <h4 className="text-base font-medium text-gray-900 mb-2">System Health Check</h4>
                <p className="text-sm text-gray-600 mb-4">Run comprehensive system diagnostics</p>
                <button className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700">
                  Run Diagnostics
                </button>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg">
                <h4 className="text-base font-medium text-gray-900 mb-2">Cache Management</h4>
                <p className="text-sm text-gray-600 mb-4">Clear system caches and temporary files</p>
                <button className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700">
                  Clear Cache
                </button>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg">
                <h4 className="text-base font-medium text-gray-900 mb-2">Log Management</h4>
                <p className="text-sm text-gray-600 mb-4">View and manage system logs</p>
                <button className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700">
                  View Logs
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 flex flex-col h-full overflow-hidden ml-16">
        <div className="flex-1 m-4 bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-8 py-4 rounded-t-2xl flex-shrink-0">
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
              <Wrench size={16} />
              <span>Tools</span>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Tools</h1>
                <p className="text-sm text-gray-600 mt-1">System administration and maintenance tools</p>
              </div>
            </div>
          </header>

          <div className="flex flex-1 overflow-hidden">
            {/* Tools Tabs */}
            <div className="w-64 bg-white border-r border-gray-200 p-6 flex-shrink-0 overflow-y-auto">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => navigate(tab.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === tab.key
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tools Content */}
            <div className="flex-1 p-8 overflow-auto bg-gray-50/50">
              <div className="max-w-7xl mx-auto h-full">
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolsPage;
